import {
	filterLanguageList,
	mapDataForPersonalDataUpdate,
} from "@utils/account-settings/personal-data";

import {
	PersonalDataResponseModel,
	UpdatePersonalDataRequestModel,
} from "src/data/types";
import {
	getCurrentCulture,
	getUserFromLocalStorage,
	isNullOrWhitespace,
} from "@utils";
import {
	AgencyUserDto,
	PlayerPromoSendingMode,
} from "@api-types/user/AgencyUserDto";

import { RegistrationLookupService } from "@services/membership/registration";
import { DateTime } from "luxon";
import { SUPPORTED_LANGUAGES } from "@common/constants/SupportedLanguages";

import { LazyImportWithLoadFailHandle as lazy } from "@lib/lazy-import-with-guard/LazyImportWithGuard";
import { createApiRequestOptions } from "@utils/api-config/createApiRequestOptions";
import {
	isBankIdInstanceMismatchError,
	BANKID_INSTANCE_MISMATCH_MESSAGE_KEY,
} from "@utils/specific/bankid/bankIdStepUpError";
import { RequestContextType } from "@utils/api-config/RequestContext";

const loadFailPath = `/${getCurrentCulture()}/app-update`;
// prettier-ignore
const AgencyUserApiService = lazy(loadFailPath, ()=>import("@api-services/user/AgencyUserApiService"));

export default class PersonalDataService {
	static async getPersonalData(
		shouldFetchLookups: boolean = true,
		shouldFetchAnnualIncomeLookup: boolean = false,
		shouldFetchStateLookup: boolean = false
	) {
		try {
			let countries = null;
			let languages = null;
			let occupation = null;
			let groupedOccupation = null;
			let countryRegions = null;
			let titles = null;
			let supportedLanguageList = null;
			let annualIncomeSpans = null;

			if (shouldFetchLookups) {
				countries = await RegistrationLookupService.getCountries();
				languages = await RegistrationLookupService.getLanguages();
				occupation = await RegistrationLookupService.getOccupations();
				groupedOccupation =
					await RegistrationLookupService.getOccupationGroups();

				titles = await RegistrationLookupService.getTitles();

				supportedLanguageList = filterLanguageList(
					SUPPORTED_LANGUAGES,
					languages
				);

				if (shouldFetchAnnualIncomeLookup) {
					annualIncomeSpans =
						await RegistrationLookupService.getAnnualIncomeSpans();
				}

				if (shouldFetchStateLookup) {
					countryRegions =
						await RegistrationLookupService.getCountryRegions(
							countries
						);
				}
			}
			const userData = await (
				await AgencyUserApiService
			).default.getUserInfo();

			const primaryUser = getUserFromLocalStorage();
			if (!primaryUser || userData.countryId == null) {
				throw { message: "GENERIC_ERROR" };
			}

			const { isInternetUser, isOfflineUserMailApproved } = primaryUser;

			const model: PersonalDataResponseModel | null = {
				data: {
					userName: userData.userName,
					firstName: userData.firstName,
					lastName: userData.lastName,
					dob: userData.dateOfBirth,
					birthPlace: userData.birthPlace,
					titleId: userData.titleId,
					countryId: userData.countryId,
					city: userData.city,
					street: userData.street,
					zipCode: userData.zipCode,
					houseNumber: userData.houseNumber,
					currentEmail:
						!isInternetUser && !isOfflineUserMailApproved
							? "---"
							: userData.email,
					mobilePhone: userData.mobilePhone,
					telephoneNumber: userData.telephoneNumber,
					language: userData.cultureInfoName,
					payPin: userData.payPin,
					occupation: userData.occupationId,
					birthName: userData.birthName,
					nationality: userData.nationality,
					receiveNewsletter:
						userData.playerPromoSendingMode ==
						PlayerPromoSendingMode.enabled
							? true
							: false,
					insicStatus: userData.insicStatus,
					regionId:
						// BE sends "0000000000000000000000" when regionId is empty,
						// but on FE we need regionId to be undefined if not specified,
						// so we're clearing it manually here
						userData.regionId !== "0000000000000000000000"
							? userData.regionId
							: undefined,
					personalIdentificator:
						userData.personalIdentificator || null,
					//@ts-expect-error
					documentExpiration: userData.documentExpiration,
					displayUserId: userData.displayUserId,
				},
				countries: countries != null ? countries : [],
				languages:
					supportedLanguageList != null ? supportedLanguageList : [],
				userTitles: titles != null ? titles : [],
				occupationData: occupation != null ? occupation : [],
				occupationGroupData:
					groupedOccupation != null ? groupedOccupation : [],
				countryRegions: countryRegions != null ? countryRegions : [],
			};
			if (shouldFetchAnnualIncomeLookup && model) {
				model.data.annualIncome = userData?.annualIncomeSpanId;
				model.annualIncomeSpans =
					annualIncomeSpans != null ? annualIncomeSpans : [];
			}
			return model;
		} catch (error) {
			console.error(error);
			if (error.message == "Unauthorized") {
				return;
			}
			throw { message: "GENERIC_ERROR" };
		}
	}

	static async updateUserPin(payPin: string) {
		if (isNullOrWhitespace(payPin)) {
			throw { message: "GENERIC_ERROR" };
		}

		try {
			await (
				await AgencyUserApiService
			).default.updateUserPin(
				payPin,
				createApiRequestOptions(RequestContextType.Shop)
			);

			return null;
		} catch (error) {
			console.error(error);
			if (error.message == "Unauthorized") {
				return new Error();
			}
			throw { message: "PERSONAL_DATA_ERR" };
		}
	}

	static async updatePersonalData(model: UpdatePersonalDataRequestModel) {
		if (model == null || model == null) {
			throw { message: "GENERIC_ERROR" };
		}

		model.dob = DateTime.fromISO(model.dob).toFormat("yyyy-MM-dd");

		if (model.documentExpiration) {
			model.documentExpiration = DateTime.fromISO(
				model.documentExpiration
			).toFormat("yyyy-MM-dd");
		}

		let agencyUser: AgencyUserDto | null = null;

		try {
			agencyUser = await (
				await AgencyUserApiService
			).default.getUserInfo();
		} catch (error) {
			console.error(error);
			if (error.message == "Unauthorized") {
				return;
			}
			throw { message: "GENERIC_ERROR" };
		}
		//@ts-expect-error
		if (model?.receiveNewsletter != agencyUser?.playerPromoSendingMode) {
			try {
				await (
					await AgencyUserApiService
				).default
					//@ts-expect-error
					.promoMaterialsApproval(model.receiveNewsletter);
			} catch (error) {
				console.error(error);

				const errorObj = await error.rawResponse.json();

				if (errorObj[0].errorCode === 400229) {
					throw {
						message:
							"MEMBERSHIP.ERR_HANDLING.SPELPAUS_MARKETING_EXCLUSION_ACTIVE",
						isFull: true,
					};
				}
			}
		}

		try {
			const response = await (
				await AgencyUserApiService
			).default.updatePersonalData(
				mapDataForPersonalDataUpdate(agencyUser, model)
			);

			if (response) {
				App.state.rootStore.countryChangeStore.activatePopup(
					response.message
				);
				return;
			}

			return model;
		} catch (error) {
			if (isBankIdInstanceMismatchError(error)) {
				throw {
					message: BANKID_INSTANCE_MISMATCH_MESSAGE_KEY,
					isFull: true,
				};
			} else if (error.errorCode == 400266) {
				throw {
					message: error.message,
					isFull: true,
				};
			} else if (error.data.errorCode == 400268) {
				throw {
					message: "error-handling:OASIS_DOWN",
					isFull: true,
				};
			} else if (error.message == "Unauthorized") {
				return;
			} else if (error.data.errorCode === 400216) {
				await App.state.rootStore.userAuthStore.logoutUser();
				App.state.history.push(
					`/${getCurrentCulture()}/auth/login?isBlocked=true`
				);
				return;
			} else if (error.data.errorCode === 400175) {
				await App.state.rootStore.userAuthStore.logoutUser();
				App.state.history.push(
					`/${getCurrentCulture()}/auth/login?isTooManyAttempts=true`
				);
				return;
			} else if (error.data.errorCode === 400176) {
				throw { message: "INVALID_PASSWORD" };
			} else if (error.data.errorCode === 400230) {
				throw {
					message:
						"MEMBERSHIP.ERR_HANDLING.SPELPAUS_USER_BLOCKED_FROM_PLAYING",
					isFull: true,
				};
			} else if (error.data.errorCode === 400242) {
				throw {
					message: "MEMBERSHIP.ERR_HANDLING.LUGAS_VALIDATION_ERROR",
					isFull: true,
				};
			} else if (error.data.errorCode === 400243) {
				throw {
					message:
						"MEMBERSHIP.ERR_HANDLING.LUGAS_PLAYER_NOT_REGISTERED",
					isFull: true,
				};
			} else if (error.data.errorCode === 400245) {
				throw {
					message: "MEMBERSHIP.ERR_HANDLING.LUGAS_DOWN",
					isFull: true,
				};
			} else if (error.data.errorCode === 400256) {
				throw {
					message: "MEMBERSHIP.ERR_HANDLING.LUGAS_UNSPECIFIED",
					isFull: true,
				};
			} else if (error.data.errorCode === 400296) {
				throw {
					message: error.data.message,
					isFull: true,
				};
			} else {
				throw { message: "GENERIC_ERROR" };
			}
		}
	}

	static async updatePersonalDataForOnlineUser(
		model: UpdatePersonalDataRequestModel
	): Promise<
		| UpdatePersonalDataRequestModel
		| { errorCode: number; message: string }
		| void
	> {
		if (model == null || model == null) {
			throw { message: "GENERIC_ERROR" };
		}

		let agencyUser: AgencyUserDto | null = null;

		try {
			agencyUser = await (
				await AgencyUserApiService
			).default.getUserInfo();
		} catch (error) {
			console.error(error);
			if (error.message == "Unauthorized") {
				return;
			}
			throw { message: "GENERIC_ERROR" };
		}

		try {
			const response = await (
				await AgencyUserApiService
			).default.updatePersonalDataWithoutPassword(
				mapDataForPersonalDataUpdate(agencyUser, model)
			);

			if (response) {
				App.state.rootStore.countryChangeStore.activatePopup(
					response.message
				);
				return {
					errorCode: 400266,
					message: "Break flow",
				};
			}

			return model;
		} catch (error) {
			if (error.errorCode == 400266) {
				throw {
					message: error.message,
					isFull: true,
				};
			} else if (error.message == "Unauthorized") {
				return;
			} else if (error.data.errorCode === 400216) {
				await App.state.rootStore.userAuthStore.logoutUser();
				App.state.history.push(
					`/${getCurrentCulture()}/auth/login?isBlocked=true`
				);
				return;
			} else if (error.data.errorCode === 400175) {
				await App.state.rootStore.userAuthStore.logoutUser();
				App.state.history.push(
					`/${getCurrentCulture()}/auth/login?isTooManyAttempts=true`
				);
				return;
			} else if (error.data.errorCode === 400176) {
				throw { message: "INVALID_PASSWORD" };
			} else if (error.data.errorCode === 400230) {
				throw {
					message:
						"MEMBERSHIP.ERR_HANDLING.SPELPAUS_USER_BLOCKED_FROM_PLAYING",
					isFull: true,
				};
			} else if (error.data.errorCode === 400242) {
				throw {
					message: "MEMBERSHIP.ERR_HANDLING.LUGAS_VALIDATION_ERROR",
					isFull: true,
				};
			} else if (error.data.errorCode === 400243) {
				throw {
					message:
						"MEMBERSHIP.ERR_HANDLING.LUGAS_PLAYER_NOT_REGISTERED",
					isFull: true,
				};
			} else if (error.data.errorCode === 400245) {
				throw {
					message: "MEMBERSHIP.ERR_HANDLING.LUGAS_DOWN",
					isFull: true,
				};
			} else if (error.data.errorCode === 400256) {
				throw {
					message: "MEMBERSHIP.ERR_HANDLING.LUGAS_UNSPECIFIED",
					isFull: true,
				};
			}
			throw { message: "GENERIC_ERROR" };
		}
	}

	static getNewPayPin() {
		return Math.floor(1000 + Math.random() * 9000);
	}
}
